/**
 * Voice AI — Guest Conversations Tab
 * Paginated conversation list with expandable transcript viewer.
 * Self-loading: fetches its own data when phone changes.
 * Reads from main voice.db calls + transcripts tables (single source of truth).
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { guestStyles } from '../../styles/guest-styles.js';
import { tz } from '../../services/timezone-service.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { guestService } from '../../services/guest-service.js';

export class GuestConversationsTab extends LitElement {
  static properties = {
    phone: { type: String },
    _conversations: { state: true },
    _loading: { state: true },
    _page: { state: true },
    _total: { state: true },
    _pages: { state: true },
    _expandedCallId: { state: true },
    _transcript: { state: true },
    _toolCalls: { state: true },
    _callDetail: { state: true },
    _transcriptLoading: { state: true },
  };

  static styles = [designSystem, componentStyles, guestStyles, css`
    /* Override shared guestStyles transcript layout — use row-based transcript, not chat bubbles */
    .transcript-list {
      display: flex;
      flex-direction: column;
      gap: 0;
      max-height: 500px;
      overflow-y: auto;
      padding: var(--space-2) 0;
    }
    .transcript-msg {
      display: flex;
      flex-direction: row;
      gap: var(--space-2);
      align-items: flex-start;
      max-width: 100%;
      padding: var(--space-2) var(--space-3);
      border-radius: var(--radius-sm);
      transition: background var(--transition-fast);
    }
    .transcript-msg:hover {
      background: rgba(99,102,241,0.03);
    }
    .transcript-msg.customer,
    .transcript-msg.agent {
      align-self: stretch;
    }
    .transcript-timestamp {
      font-family: var(--font-mono);
      font-size: 11px;
      color: var(--color-text-tertiary);
      min-width: 40px;
      flex-shrink: 0;
      font-variant-numeric: tabular-nums;
      padding-top: 1px;
    }
    .transcript-speaker {
      font-size: 11px;
      font-weight: var(--font-semibold);
      min-width: 64px;
      flex-shrink: 0;
      text-transform: none;
      letter-spacing: normal;
      padding: 0;
      margin-bottom: 0;
      padding-top: 1px;
    }
    .transcript-speaker.customer {
      color: var(--color-text-secondary);
    }
    .transcript-speaker.agent {
      color: #6366f1;
    }
    .transcript-bubble {
      flex: 1;
      padding: 0;
      border: none;
      border-radius: 0;
      background: none;
      font-size: var(--text-sm);
      line-height: 1.5;
      color: var(--color-text-primary);
    }
    .transcript-msg.customer .transcript-bubble,
    .transcript-msg.agent .transcript-bubble {
      background: none;
      border: none;
      border-radius: 0;
    }
    .transcript-confidence {
      font-size: 10px;
      color: var(--color-text-tertiary);
      opacity: 0.6;
      margin-left: var(--space-1);
    }
    .tool-calls-section {
      margin-top: var(--space-3);
      padding-top: var(--space-3);
      border-top: 1px solid rgba(99,102,241,0.08);
    }
    .tool-calls-title {
      font-size: var(--text-xs);
      font-weight: 600;
      color: var(--color-text-secondary);
      margin-bottom: var(--space-2);
      display: flex;
      align-items: center;
      gap: var(--space-1);
    }
    .tool-item {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      padding: var(--space-1) var(--space-2);
      font-size: var(--text-xs);
      background: rgba(99,102,241,0.04);
      border-radius: var(--radius-sm);
      margin-bottom: 2px;
    }
    .tool-item .tool-timestamp {
      font-family: var(--font-mono);
      font-size: 11px;
      color: var(--color-text-tertiary);
      min-width: 40px;
      flex-shrink: 0;
      font-variant-numeric: tabular-nums;
    }
    .tool-item .tool-name {
      font-weight: 500;
      color: var(--color-text-primary);
    }
    .tool-item .tool-time {
      color: var(--color-text-tertiary);
      margin-left: auto;
    }
    .tool-item .tool-status {
      font-size: 10px;
      padding: 1px 6px;
      border-radius: 8px;
      background: rgba(16,185,129,0.1);
      color: rgb(16,185,129);
    }
    .tool-item .tool-status.error {
      background: rgba(239,68,68,0.1);
      color: rgb(239,68,68);
    }
    .call-detail-meta {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-2);
      margin-bottom: var(--space-3);
      font-size: var(--text-xs);
      color: var(--color-text-tertiary);
    }
    .call-detail-meta span {
      background: rgba(99,102,241,0.05);
      padding: 2px 8px;
      border-radius: var(--radius-sm);
    }
  `];

  constructor() {
    super();
    this.phone = null;
    this._conversations = [];
    this._loading = true;
    this._page = 1;
    this._total = 0;
    this._pages = 0;
    this._expandedCallId = null;
    this._transcript = [];
    this._toolCalls = [];
    this._callDetail = null;
    this._transcriptLoading = false;
    this._lastPhone = null;
  }

  updated(changed) {
    if (changed.has('phone') && this.phone && this.phone !== this._lastPhone) {
      this._lastPhone = this.phone;
      this._page = 1;
      this._loadConversations();
    }
  }

  async _loadConversations() {
    this._loading = true;
    try {
      const data = await guestService.getConversations(this.phone, {
        page: this._page,
        limit: 20,
      });
      if (data.success) {
        this._conversations = data.conversations || [];
        this._total = data.total || 0;
        this._pages = data.pages || 0;
      }
    } catch (err) {
      console.error('[Conversations] Load failed:', err);
    } finally {
      this._loading = false;
    }
  }

  async _toggleExpand(callId) {
    if (this._expandedCallId === callId) {
      this._expandedCallId = null;
      this._transcript = [];
      this._toolCalls = [];
      this._callDetail = null;
      return;
    }

    this._expandedCallId = callId;
    this._transcriptLoading = true;
    this._transcript = [];
    this._toolCalls = [];
    this._callDetail = null;

    try {
      const data = await guestService.getConversationDetail(this.phone, callId);
      if (data.success) {
        this._transcript = data.transcripts || [];
        this._toolCalls = data.toolCalls || [];
        this._callDetail = data.call || null;
      }
    } catch (err) {
      console.error('[Conversations] Transcript load failed:', err);
    } finally {
      this._transcriptLoading = false;
    }
  }

  _goToPage(page) {
    if (page < 1 || page > this._pages) return;
    this._page = page;
    this._expandedCallId = null;
    this._loadConversations();
  }

  _formatDate(dateStr) {
    if (!dateStr) return '';
    return tz.formatDateTime(dateStr);
  }

  /** Format elapsed ms from call start as mm:ss */
  _formatElapsed(timestampMs, firstTimestampMs) {
    if (!timestampMs) return '';
    const elapsed = timestampMs - (firstTimestampMs || 0);
    if (elapsed < 0) return '0:00';
    const totalSec = Math.floor(elapsed / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  render() {
    if (this._loading) {
      return html`
        ${iconStyles}
        <div class="loading-spinner">
          <i class="fa-sharp fa-light fa-spinner-third"></i>
          Loading conversations...
        </div>
      `;
    }

    if (this._conversations.length === 0) {
      return html`
        ${iconStyles}
        <div class="glass-card" style="text-align:center;padding:var(--space-10);">
          <i class="fa-sharp fa-light fa-messages" style="font-size:2rem;opacity:0.3;margin-bottom:var(--space-3);display:block;"></i>
          <div style="color:var(--color-text-secondary);font-size:var(--text-sm);">No conversations yet</div>
        </div>
      `;
    }

    return html`
      ${iconStyles}
      <div class="glass-card">
        <div class="section-title">Call History</div>
        <div class="section-desc">${this._total} conversation${this._total !== 1 ? 's' : ''}</div>

        ${this._conversations.map(conv => html`
          <div class="conversation-item" @click=${() => this._toggleExpand(conv.id)}>
            <div class="conv-icon">
              <i class="fa-sharp fa-light ${conv.direction === 'outbound' ? 'fa-phone-arrow-up-right' : 'fa-phone-arrow-down-left'}"></i>
            </div>
            <div class="conv-info">
              <div class="conv-summary">${conv.summary || 'Call ' + (conv.direction || 'inbound')}</div>
              <div class="conv-meta">
                <span>${this._formatDate(conv.startedAt)}</span>
                ${conv.agentName ? html`<span>${conv.agentName}</span>` : ''}
                ${conv.outcome ? html`<span class="conv-outcome">${conv.outcome}</span>` : ''}
              </div>
            </div>
            <div class="conv-duration">${guestService.formatDuration(conv.durationSeconds)}</div>
          </div>

          ${this._expandedCallId === conv.id ? html`
            <div style="padding:var(--space-3) var(--space-4);background:rgba(99,102,241,0.03);border-radius:var(--radius-md);margin-bottom:var(--space-2);">
              ${this._transcriptLoading ? html`
                <div class="loading-spinner" style="padding:var(--space-4);">
                  <i class="fa-sharp fa-light fa-spinner-third"></i>
                  Loading transcript...
                </div>
              ` : html`
                ${this._callDetail ? html`
                  <div class="call-detail-meta">
                    ${this._callDetail.callSid ? html`<span>SID: ${this._callDetail.callSid}</span>` : ''}
                    ${this._callDetail.bookingId ? html`<span>Booking #${this._callDetail.bookingId}</span>` : ''}
                    ${this._callDetail.status ? html`<span>${this._callDetail.status}</span>` : ''}
                  </div>
                ` : ''}

                ${this._transcript.length === 0 ? html`
                  <div style="text-align:center;padding:var(--space-4);color:var(--color-text-tertiary);font-size:var(--text-sm);font-style:italic;">
                    No transcript available
                  </div>
                ` : html`
                  <div class="transcript-list">
                    ${this._transcript.map(msg => html`
                      <div class="transcript-msg ${msg.speaker}">
                        <span class="transcript-timestamp">${this._formatElapsed(msg.timestampMs, this._transcript[0]?.timestampMs)}</span>
                        <span class="transcript-speaker ${msg.speaker}">${msg.speaker === 'customer' ? 'Customer' : 'Agent'}</span>
                        <span class="transcript-bubble">
                          ${msg.content}
                          ${msg.confidence != null && msg.confidence < 0.8 ? html`<span class="transcript-confidence">${Math.round(msg.confidence * 100)}%</span>` : ''}
                        </span>
                      </div>
                    `)}
                  </div>
                `}

                ${this._toolCalls.length > 0 ? html`
                  <div class="tool-calls-section">
                    <div class="tool-calls-title">
                      <i class="fa-sharp fa-light fa-wrench"></i>
                      AI Actions (${this._toolCalls.length})
                    </div>
                    ${this._toolCalls.map(tc => {
                      const tcMs = tc.executedAt ? new Date(tc.executedAt).getTime() : 0;
                      const refMs = this._transcript.length > 0 ? this._transcript[0].timestampMs : 0;
                      return html`
                      <div class="tool-item">
                        ${tcMs && refMs ? html`<span class="tool-timestamp">${this._formatElapsed(tcMs, refMs)}</span>` : ''}
                        <span class="tool-name">${tc.toolName}</span>
                        ${tc.executionTimeMs ? html`<span class="tool-time">${tc.executionTimeMs}ms</span>` : ''}
                        <span class="tool-status ${tc.status === 'error' ? 'error' : ''}">${tc.status}</span>
                      </div>
                    `;})}

                  </div>
                ` : ''}
              `}
            </div>
          ` : ''}
        `)}

        ${this._pages > 1 ? html`
          <div class="pagination">
            <div class="pagination-info">Page ${this._page} of ${this._pages}</div>
            <div class="pagination-controls">
              <button class="page-btn" ?disabled=${this._page <= 1} @click=${() => this._goToPage(this._page - 1)}>
                <i class="fa-sharp fa-light fa-chevron-left"></i>
              </button>
              <button class="page-btn" ?disabled=${this._page >= this._pages} @click=${() => this._goToPage(this._page + 1)}>
                <i class="fa-sharp fa-light fa-chevron-right"></i>
              </button>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }
}

customElements.define('guest-conversations-tab', GuestConversationsTab);
